/**
 * Purchase Order API
 *
 * This module provides functions for Purchase Order CRUD operations:
 * - listPurchaseOrders: Get paginated list of POs
 * - getPurchaseOrder: Get single PO by ID
 * - createPurchaseOrder: Create new PO (after human review)
 * - updatePurchaseOrder: Update existing PO
 * - updateStatus: Change PO status
 * - deletePurchaseOrder: Delete a PO
 *
 * All operations are scoped to the authenticated user's company.
 */

import api from './index';

/**
 * List purchase orders
 *
 * Retrieves a paginated list of purchase orders for the user's company.
 * Supports filtering by status and pagination.
 *
 * @param {Object} [params={}] - Query parameters
 * @param {number} [params.page=1] - Page number (1-indexed)
 * @param {number} [params.limit=20] - Items per page (max 100)
 * @param {string} [params.status] - Filter by status (DRAFT, APPROVED, ARCHIVED)
 *
 * @returns {Promise<Object>} Response with POs and pagination metadata
 *
 * @example
 * // Get first page of approved POs
 * const result = await listPurchaseOrders({ status: 'APPROVED', page: 1 });
 * // result.data - Array of purchase orders
 * // result.meta.total - Total number of POs
 * // result.meta.totalPages - Total number of pages
 */
export const listPurchaseOrders = (params = {}) => {
  return api.get('/purchase-orders', { params });
};

/**
 * Get a single purchase order
 *
 * Retrieves a purchase order by its MongoDB ObjectId.
 *
 * @param {string} id - Purchase order ID
 *
 * @returns {Promise<Object>} Response with PO data
 *
 * @example
 * const result = await getPurchaseOrder('507f1f77bcf86cd799439011');
 * // result.data - Full purchase order object
 */
export const getPurchaseOrder = (id) => {
  return api.get(`/purchase-orders/${id}`);
};

/**
 * Create a new purchase order
 *
 * Creates a purchase order from validated data.
 * This is typically called after the user reviews and approves AI-extracted data.
 *
 * @param {Object} data - Purchase order data
 * @param {Object} data.header - Header information
 * @param {string} data.header.poNumber - PO number (unique per company)
 * @param {string} [data.header.date] - PO date (YYYY-MM-DD)
 * @param {string} [data.header.currency] - Currency code (default: USD)
 * @param {string} [data.header.paymentTerms] - Payment terms
 * @param {Array} [data.parties] - Array of party objects
 * @param {Array} [data.items] - Array of line item objects
 * @param {string} [data.status] - Status (default: DRAFT)
 * @param {string} [data.originalDocumentUrl] - URL to source document
 * @param {Object} [data.extractionConfidence] - AI confidence scores
 *
 * @returns {Promise<Object>} Response with created PO
 *
 * @example
 * const result = await createPurchaseOrder({
 *   header: { poNumber: 'PO-2024-001', date: '2024-01-15' },
 *   parties: [{ type: 'VENDOR', name: 'Supplier Inc' }],
 *   items: [{ lineId: '1', quantity: 10, unitPrice: 25.00 }],
 *   status: 'APPROVED',
 *   originalDocumentUrl: 'http://localhost:3000/uploads/po-123.pdf'
 * });
 */
export const createPurchaseOrder = (data) => {
  return api.post('/purchase-orders', data);
};

/**
 * Update an existing purchase order
 *
 * Updates a purchase order with new data.
 * All provided fields will be replaced.
 *
 * @param {string} id - Purchase order ID
 * @param {Object} data - Updated PO data (same structure as create)
 *
 * @returns {Promise<Object>} Response with updated PO
 */
export const updatePurchaseOrder = (id, data) => {
  return api.put(`/purchase-orders/${id}`, data);
};

/**
 * Update purchase order status
 *
 * Changes only the status of a purchase order.
 * Useful for workflow transitions (DRAFT -> APPROVED -> ARCHIVED).
 *
 * @param {string} id - Purchase order ID
 * @param {string} status - New status (DRAFT, APPROVED, or ARCHIVED)
 *
 * @returns {Promise<Object>} Response with updated PO
 *
 * @example
 * // Approve a draft PO
 * await updateStatus('507f1f77bcf86cd799439011', 'APPROVED');
 */
export const updateStatus = (id, status) => {
  return api.patch(`/purchase-orders/${id}/status`, { status });
};

/**
 * Delete a purchase order
 *
 * Permanently deletes a purchase order.
 * Consider using updateStatus to ARCHIVED instead for soft delete.
 *
 * @param {string} id - Purchase order ID
 *
 * @returns {Promise<Object>} Success message
 */
export const deletePurchaseOrder = (id) => {
  return api.delete(`/purchase-orders/${id}`);
};

/**
 * Search purchase orders
 *
 * Full-text search across purchase order content including sections.
 * Supports filtering by section type.
 *
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.q] - Full-text search query
 * @param {string} [params.sectionType] - Filter by section type (e.g., 'terms_and_conditions')
 * @param {number} [params.page=1] - Page number (1-indexed)
 * @param {number} [params.limit=20] - Items per page (max 100)
 *
 * @returns {Promise<Object>} Response with matching POs and pagination metadata
 *
 * @example
 * // Search for POs mentioning "expedited shipping"
 * const result = await searchPurchaseOrders({ q: 'expedited shipping' });
 *
 * @example
 * // Find POs with special instructions sections
 * const result = await searchPurchaseOrders({ sectionType: 'special_instructions' });
 */
export const searchPurchaseOrders = (params = {}) => {
  return api.get('/purchase-orders/search', { params });
};

/**
 * Get purchase order statistics
 *
 * Returns aggregated stats (count, total amount) for filtered POs.
 *
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.status] - Filter by status
 * @param {string} [params.dateFrom] - Filter POs with date >= dateFrom (YYYY-MM-DD)
 * @param {string} [params.dateTo] - Filter POs with date <= dateTo (YYYY-MM-DD)
 * @param {string} [params.vendor] - Filter by vendor name
 *
 * @returns {Promise<Object>} Response with { count, totalAmount }
 */
export const getStats = (params = {}) => {
  return api.get('/purchase-orders/stats', { params });
};

/**
 * Get list of vendors
 *
 * Returns unique vendor names for filter dropdown.
 *
 * @returns {Promise<Object>} Response with array of vendor names
 */
export const getVendors = () => {
  return api.get('/purchase-orders/vendors');
};

/**
 * Get spend by period (month or week)
 *
 * Returns spend data aggregated by time period for chart visualization.
 *
 * @param {Object} [params={}] - Query parameters
 * @param {string} [params.period='month'] - Period type ('week' or 'month')
 * @param {number} [params.months=12] - Number of months to look back (1-24)
 * @param {string} [params.status] - Filter by status
 *
 * @returns {Promise<Object>} Response with array of { label, totalSpend, poCount }
 */
export const getSpendByPeriod = (params = {}) => {
  return api.get('/purchase-orders/stats/spend-by-period', { params });
};
